import { defineStore } from 'pinia'
import { UnleashClient } from '@unleash/proxy-client-vue'
import type { FeatureFlagsState, UnleashContext, UnleashConfig } from '@/types/featureFlags'
import { FeatureFlag } from '@/types/featureFlags'

// Unleash client configuration from environment variables (static at build time)
const unleashConfig: UnleashConfig = {
  url: import.meta.env.VITE_UNLEASH_URL,
  clientKey: import.meta.env.VITE_UNLEASH_CLIENT_KEY,
  appName: import.meta.env.VITE_UNLEASH_APP_NAME,
  environment: import.meta.env.VITE_UNLEASH_ENVIRONMENT || 'development',
  refreshInterval: 30,
}

// Validate configuration at module load
if (!unleashConfig.url || !unleashConfig.clientKey || !unleashConfig.appName) {
  console.error('Unleash configuration incomplete:', {
    hasUrl: !!unleashConfig.url,
    hasClientKey: !!unleashConfig.clientKey,
    hasAppName: !!unleashConfig.appName,
  })
  throw new Error('Missing required Unleash environment variables')
}

interface FeatureFlagsStoreState extends FeatureFlagsState {
  _client: UnleashClient | null
}

export const useFeatureFlagsStore = defineStore('featureFlags', {
  state: (): FeatureFlagsStoreState => ({
    initialized: false,
    loading: true,
    error: null,
    flags: {},
    _client: null,
  }),

  getters: {
    /**
     * Check if a feature flag is enabled
     * @param flagName - The name of the feature flag
     * @returns boolean indicating if the flag is enabled
     */
    isEnabled: (state) => (flagName: string | FeatureFlag): boolean => {
      return state.flags[flagName] ?? false
    },

    /**
     * Get all enabled flags
     */
    enabledFlags: (state): string[] => {
      return Object.entries(state.flags)
        .filter(([_, enabled]) => enabled)
        .map(([name]) => name)
    },

    /**
     * Check if the Unleash client is ready
     */
    isReady: (state): boolean => {
      return state.initialized && !state.loading && !state.error
    },
  },

  actions: {
    /**
     * Initialize the Unleash client
     */
    async initialize(context?: UnleashContext) {
      try {
        this.loading = true
        this.error = null

        // Create new Unleash client if not already created
        if (!this._client) {
          this._client = new UnleashClient({
            url: unleashConfig.url,
            clientKey: unleashConfig.clientKey,
            appName: unleashConfig.appName,
            environment: unleashConfig.environment,
            refreshInterval: unleashConfig.refreshInterval,
            context: context,
          })

          // Set up event listeners (only once when client is created)
          this._client.on('ready', () => {
            this.initialized = true
            this.loading = false
            this.syncFlags()
          })

          this._client.on('update', () => {
            this.syncFlags()
          })

          this._client.on('error', (error: Error) => {
            console.error('Unleash error:', error)
            this.error = error.message
            this.loading = false
          })

          // Start the client
          await this._client.start()
        }

        return true
      } catch (error) {
        console.error('Failed to initialize Unleash:', error)
        this.error = error instanceof Error ? error.message : 'Unknown error'
        this.loading = false
        this.initialized = false
        return false
      }
    },

    /**
     * Sync all feature flags from Unleash client to store
     */
    syncFlags() {
      if (!this._client) return

      const allFlags: Record<string, boolean> = {}

      // Get all known feature flags from the enum
      Object.values(FeatureFlag).forEach((flagName) => {
        allFlags[flagName] = this._client!.isEnabled(flagName)
      })

      // Also sync any other flags that might exist
      const toggles = this._client.getAllToggles()
      toggles.forEach((toggle) => {
        allFlags[toggle.name] = toggle.enabled
      })

      this.flags = allFlags
    },

    /**
     * Update the Unleash context (e.g., when user logs in)
     * @param context - The new context to apply
     */
    async updateContext(context: UnleashContext) {
      if (!this._client) {
        console.warn('Unleash client not initialized')
        return
      }

      try {
        await this._client.updateContext(context)
        this.syncFlags()
      } catch (error) {
        console.error('Failed to update Unleash context:', error)
      }
    },

    /**
     * Get variant for a feature flag
     * @param flagName - The name of the feature flag
     */
    getVariant(flagName: string | FeatureFlag) {
      if (!this._client) {
        return null
      }
      return this._client.getVariant(flagName)
    },

    /**
     * Stop the Unleash client (cleanup)
     */
    async stop() {
      if (this._client) {
        try {
          await this._client.stop()
          this._client = null
          this.initialized = false
          this.loading = false
          this.flags = {}
        } catch (error) {
          console.error('Error stopping Unleash client:', error)
        }
      }
    },
  },
})

